import { create } from 'zustand';
import type { TenantId, TenantState } from './types';
import { parseTenantId, type RuntimeConfig } from './runtime-config';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';
import { useUserStore } from './user.store';
import { usePatientStore } from './patient.store';
import { getPatientsForTenant } from '../data/synthetic-patients';

/**
 * Tenant store (PROD-568).
 *
 * The store ships with a sync default (`apex-cardiometabolic`) so the app
 * can render before the runtime config fetch resolves. `main.tsx` awaits
 * `loadRuntimeConfig()` and then calls `hydrateFromConfig(cfg)` to update
 * the tenant default from the K8s-injected `/config.json`.
 *
 * `setTenant` validates the incoming id against the Zod-derived enum;
 * unknown ids log a warning and no-op so a typo cannot wedge the store
 * into an invalid state.
 *
 * PROD-568 wires four contracts (the fourth was added in the 2026-05-05
 * fixer round):
 *   1. localStorage persistence per ADR-PROTO-006
 *      (`cps:${userId}:tenant:active`).
 *   2. Cross-store invariant (SPEC §5.4 invariant 2 + PROD-579 review):
 *      switching the tenant moves the active patient id to a
 *      tenant-appropriate patient via `getPatientsForTenant(newTenant)[0]`.
 *      The brief overrides the SPEC's "clear active patient" wording with
 *      "set to the first patient in the new tenant's filtered list" so the
 *      picker always has a valid selection.
 *   3. Telemetry: `setTenant` emits `tenant_change` with payload
 *      `{ from_tenant, to_tenant, latency_ms, correlation_id }`. Latency is
 *      a real `performance.now()` delta; the < 100ms perf budget is
 *      enforced in Phase 3 Playwright per HC1.
 *   4. Persistence-error revert (COMPONENT-CONTRACTS §2 live-mode failure
 *      surface): if `localStorage.setItem` throws (quota / private
 *      browsing), the tenant id AND the cross-store patient mutation are
 *      reverted to the prior state, no telemetry is emitted, and
 *      `persistError` is populated so the switcher renders a banner /
 *      toast surfacing "Tenant preference could not be persisted. Switch
 *      will reset on refresh."
 */
const DEFAULT_TENANT: TenantId = 'apex-cardiometabolic';

export interface ExtendedTenantState extends TenantState {
  /**
   * Transient persist-error message. Non-null when the most recent
   * `setTenant` attempt rolled back because `localStorage.setItem` threw.
   * Cleared by `clearPersistError`, by a successful subsequent switch, or
   * by user dismissal in the switcher UI.
   */
  persistError: string | null;
  /** Clear the transient `persistError` (called on user dismissal). */
  clearPersistError: () => void;
  hydrateFromConfig: (cfg: RuntimeConfig) => void;
}

/** localStorage key per ADR-PROTO-006 userId-keying convention. */
export function tenantStorageKey(userId: string): string {
  return `cps:${userId}:tenant:active`;
}

/**
 * Read the persisted active tenant id for the current user. Returns
 * `null` if no value is persisted, the persisted id is unknown, or
 * localStorage is unavailable (private browsing, server render).
 */
function readPersistedTenantId(): TenantId | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const userId = useUserStore.getState().userId;
    const raw = localStorage.getItem(tenantStorageKey(userId));
    if (!raw) return null;
    return parseTenantId(raw);
  } catch (err) {
    console.warn('[tenant.store] localStorage read failed; using default', err);
    return null;
  }
}

/**
 * Persist the active tenant id for the current user. Throws on any
 * underlying `localStorage.setItem` failure (quota exceeded, private
 * browsing) so the calling action can roll back the in-memory state per
 * COMPONENT-CONTRACTS §2 live-mode failure surface.
 */
function persistTenantId(id: TenantId): void {
  if (typeof localStorage === 'undefined') {
    // Server-render / non-browser env: nothing to persist; treat as success.
    return;
  }
  const userId = useUserStore.getState().userId;
  localStorage.setItem(tenantStorageKey(userId), id);
}

const PERSIST_ERROR_MESSAGE =
  'Tenant preference could not be persisted. Switch will reset on refresh.';

const INITIAL_TENANT: TenantId = readPersistedTenantId() ?? DEFAULT_TENANT;

export const useTenantStore = create<ExtendedTenantState>((set, get) => ({
  tenantId: INITIAL_TENANT,
  persistError: null,
  setTenant: (tenantId) => {
    const valid = parseTenantId(tenantId);
    if (!valid) {
      console.warn(
        `[tenant.store] setTenant rejected unknown id "${String(tenantId)}"; keeping current value`,
      );
      return;
    }
    const previousTenant = get().tenantId;
    if (previousTenant === valid) return;

    // Snapshot the cross-store patient state BEFORE mutating so we can
    // revert atomically if persistence throws.
    const previousPatientId = usePatientStore.getState().activePatientId;

    const start = performance.now();
    set({ tenantId: valid });

    // Cross-store invariant: move the active patient to a patient that
    // belongs to the newly selected tenant so the picker never points at
    // a patient outside the filtered list. Per the brief this overrides
    // the SPEC §5.4 "clear" wording.
    const tenantPatients = getPatientsForTenant(valid);
    if (tenantPatients.length > 0) {
      usePatientStore.getState().setActivePatientId(tenantPatients[0].id);
    }

    try {
      persistTenantId(valid);
    } catch (err) {
      // Revert in-memory state + cross-store mutation; surface the
      // persist error so the switcher can render the banner / toast.
      console.warn(
        '[tenant.store] persist failed; reverting tenant + active patient',
        err,
      );
      set({ tenantId: previousTenant, persistError: PERSIST_ERROR_MESSAGE });
      if (previousPatientId !== null) {
        usePatientStore.getState().setActivePatientId(previousPatientId);
      }
      // Do NOT emit `tenant_change` telemetry on a reverted switch.
      return;
    }

    // Successful persist: clear any stale persist error from a prior
    // failed attempt.
    if (get().persistError !== null) {
      set({ persistError: null });
    }

    const latency = Math.round(performance.now() - start);
    emitTelemetry('tenant_change', {
      from_tenant: previousTenant,
      to_tenant: valid,
      latency_ms: latency,
      correlation_id: newCorrelationId(),
    });
  },
  clearPersistError: () => {
    if (get().persistError !== null) {
      set({ persistError: null });
    }
  },
  hydrateFromConfig: (cfg) => {
    // Honor a previously-persisted tenant over the runtime config default.
    // This way a clinician's last selection survives a redeploy.
    const persisted = readPersistedTenantId();
    set({ tenantId: persisted ?? cfg.tenantDefault });
  },
}));
